import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';

interface Produto {
  id: string;
  codigo_produto: string;
  nome_produto: string;
  categoria: string;
  valor: number;
}

interface Movimentacao {
  id: string;
  produto_id: string;
  tipo_movimentacao: 'entrada' | 'saida';
  quantidade: number;
  saldo_atual: number;
  data_movimentacao: string;
  observacoes?: string;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  produtos_insumos?: Produto;
}

export default function MovimentacoesEstoque() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof Movimentacao | "">("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedMovimentacao, setSelectedMovimentacao] = useState<Movimentacao | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    produto_id: '',
    produto_search: '',
    tipo_movimentacao: '' as 'entrada' | 'saida' | '',
    quantidade: '',
    observacoes: '',
  });

  const [filteredProdutos, setFilteredProdutos] = useState<Produto[]>([]);
  const [showProdutoSuggestions, setShowProdutoSuggestions] = useState(false);

  const [movimentacoes, setMovimentacoes] = useState<Movimentacao[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);

  // Filter produtos based on search
  useEffect(() => {
    if (formData.produto_search) {
      const filtered = produtos.filter(produto =>
        produto.nome_produto.toLowerCase().includes(formData.produto_search.toLowerCase()) ||
        produto.codigo_produto.toLowerCase().includes(formData.produto_search.toLowerCase())
      );
      setFilteredProdutos(filtered);
      setShowProdutoSuggestions(true);
    } else {
      setFilteredProdutos([]);
      setShowProdutoSuggestions(false);
    }
  }, [formData.produto_search, produtos]);

  // Permission checks
  const canReadMovimentacoes = !permissionsLoading && hasPermission('produtos_insumos', 'movimentacoes', 'read');
  const canWriteMovimentacoes = !permissionsLoading && hasPermission('produtos_insumos', 'movimentacoes', 'write');
  const canAdminMovimentacoes = !permissionsLoading && hasPermission('produtos_insumos', 'movimentacoes', 'admin');

  useEffect(() => {
    if (!permissionsLoading) {
      if (canReadMovimentacoes) {
        fetchMovimentacoes();
        fetchProdutos();
      } else {
        setLoading(false);
      }
    }
  }, [permissionsLoading, canReadMovimentacoes]);

  const fetchMovimentacoes = async () => {
    try {
      const { data, error } = await supabase
        .from('produtos_entrada_saida')
        .select(`
          *,
          produtos_insumos (
            id,
            codigo_produto,
            nome_produto,
            categoria,
            valor
          )
        `)
        .order('data_movimentacao', { ascending: false });

      if (error) throw error;
      setMovimentacoes(data as Movimentacao[] || []);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Erro ao carregar movimentações: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProdutos = async () => {
    try {
      // @ts-ignore - Temporary fix for Supabase type inference issue
      const { data, error }: { data: any, error: any } = await supabase
        .from('produtos_insumos')
        .select('id, codigo_produto, nome_produto, categoria, valor')
        .eq('ativo', true)
        .order('nome_produto');

      if (error) throw error;
      setProdutos(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const calculateSaldoAtual = async (produtoId: string, quantidade: number, tipoMovimentacao: 'entrada' | 'saida') => {
    try {
      const { data, error } = await supabase
        .from('produtos_entrada_saida')
        .select('saldo_atual')
        .eq('produto_id', produtoId)
        .order('data_movimentacao', { ascending: false })
        .limit(1);

      if (error) throw error;

      const saldoAnterior = data?.[0]?.saldo_atual || 0;
      return tipoMovimentacao === 'entrada' 
        ? saldoAnterior + quantidade
        : saldoAnterior - quantidade;
    } catch (error) {
      console.error('Erro ao calcular saldo:', error);
      return quantidade;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Erro",
        description: "Usuário não autenticado",
        variant: "destructive",
      });
      return;
    }

    if (!formData.tipo_movimentacao) {
      toast({
        title: "Erro",
        description: "Tipo de movimentação é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (!formData.produto_id) {
      toast({
        title: "Erro",
        description: "Produto é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      const quantidade = parseInt(formData.quantidade);
      const saldoAtual = await calculateSaldoAtual(
        formData.produto_id, 
        quantidade, 
        formData.tipo_movimentacao as 'entrada' | 'saida'
      );

      if (saldoAtual < 0) {
        toast({
          title: "Erro",
          description: "Saldo insuficiente para realizar esta saída",
          variant: "destructive",
        });
        return;
      }

      const movimentacaoData = {
        produto_id: formData.produto_id,
        tipo_movimentacao: formData.tipo_movimentacao,
        quantidade,
        saldo_atual: saldoAtual,
        observacoes: formData.observacoes || null,
        created_by: user.id,
        updated_by: user.id,
      };

      let result;
      if (isEditMode && selectedMovimentacao) {
        result = await supabase
          .from('produtos_entrada_saida')
          .update({ ...movimentacaoData, updated_by: user.id })
          .eq('id', selectedMovimentacao.id);
      } else {
        result = await supabase
          .from('produtos_entrada_saida')
          .insert([movimentacaoData]);
      }

      if (result.error) throw result.error;

      toast({
        title: "Sucesso",
        description: `Movimentação ${isEditMode ? 'atualizada' : 'criada'} com sucesso!`,
      });

      fetchMovimentacoes();
      resetForm();
      setIsDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: `Erro ao ${isEditMode ? 'atualizar' : 'criar'} movimentação: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (movimentacao: Movimentacao) => {
    try {
      const { error } = await supabase
        .from('produtos_entrada_saida')
        .delete()
        .eq('id', movimentacao.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Movimentação excluída com sucesso!",
      });

      fetchMovimentacoes();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: "Erro ao excluir movimentação: " + error.message,
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      produto_id: '',
      produto_search: '',
      tipo_movimentacao: '',
      quantidade: '',
      observacoes: '',
    });
    setIsEditMode(false);
    setSelectedMovimentacao(null);
    setShowProdutoSuggestions(false);
  };

  const handleEdit = (movimentacao: Movimentacao) => {
    const produto = produtos.find(p => p.id === movimentacao.produto_id);
    setFormData({
      produto_id: movimentacao.produto_id,
      produto_search: produto ? `${produto.codigo_produto} - ${produto.nome_produto}` : '',
      tipo_movimentacao: movimentacao.tipo_movimentacao,
      quantidade: movimentacao.quantidade.toString(),
      observacoes: movimentacao.observacoes || '',
    });
    setSelectedMovimentacao(movimentacao);
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  const handleView = (movimentacao: Movimentacao) => {
    setSelectedMovimentacao(movimentacao);
    setIsViewDialogOpen(true);
  };

  const handleSort = (field: keyof Movimentacao) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredMovimentacoes = movimentacoes.filter(mov =>
    mov.produtos_insumos?.nome_produto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mov.produtos_insumos?.codigo_produto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mov.tipo_movimentacao.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mov.observacoes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedMovimentacoes = [...filteredMovimentacoes].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];
    
    if (sortField === 'data_movimentacao') {
      aValue = new Date(aValue as string).getTime();
      bValue = new Date(bValue as string).getTime();
    }
    
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  if (permissionsLoading || loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  if (!canReadMovimentacoes) {
    return (
      <div className="text-center p-8">
        <h3 className="text-lg font-semibold mb-2">Acesso Negado</h3>
        <p className="text-muted-foreground">Você não tem permissão para visualizar movimentações de estoque.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Movimentações de Estoque</h1>
        {canWriteMovimentacoes && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Movimentação
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {isEditMode ? 'Editar Movimentação' : 'Nova Movimentação'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Label htmlFor="produto_search">Produto</Label>
                  <Input
                    id="produto_search"
                    placeholder="Digite o nome ou código do produto..."
                    value={formData.produto_search}
                    onChange={(e) => setFormData({ ...formData, produto_search: e.target.value })}
                    required
                  />
                  {showProdutoSuggestions && filteredProdutos.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredProdutos.map((produto) => (
                        <div
                          key={produto.id}
                          className="px-3 py-2 cursor-pointer hover:bg-muted"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              produto_id: produto.id,
                              produto_search: `${produto.codigo_produto} - ${produto.nome_produto}`
                            });
                            setShowProdutoSuggestions(false);
                          }}
                        >
                          <div className="font-medium">{produto.nome_produto}</div>
                          <div className="text-sm text-muted-foreground">
                            {produto.codigo_produto} - {produto.categoria}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Tipo de Movimentação</Label>
                  <RadioGroup
                    value={formData.tipo_movimentacao}
                    onValueChange={(value: 'entrada' | 'saida') => 
                      setFormData({ ...formData, tipo_movimentacao: value })
                    }
                    className="flex gap-6 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="entrada" id="entrada" />
                      <Label htmlFor="entrada" className="cursor-pointer">Entrada</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="saida" id="saida" />
                      <Label htmlFor="saida" className="cursor-pointer">Saída</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label htmlFor="quantidade">Quantidade</Label>
                  <Input
                    id="quantidade"
                    type="number"
                    value={formData.quantidade}
                    onChange={(e) => setFormData({ ...formData, quantidade: e.target.value })}
                    min="1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <Input
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Observações (opcional)"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {isEditMode ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <div className="flex-1">
              <Label htmlFor="search">Buscar</Label>
              <Input
                id="search"
                placeholder="Buscar por produto, código, tipo ou observações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Movimentações</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer"
                  onClick={() => handleSort('data_movimentacao')}
                >
                  Data
                  {sortField === 'data_movimentacao' && (
                    sortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4 inline" /> : <ChevronDown className="ml-1 h-4 w-4 inline" />
                  )}
                </TableHead>
                <TableHead>Produto</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('quantidade')}>
                  Quantidade
                  {sortField === 'quantidade' && (
                    sortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4 inline" /> : <ChevronDown className="ml-1 h-4 w-4 inline" />
                  )}
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('saldo_atual')}>
                  Saldo Atual
                  {sortField === 'saldo_atual' && (
                    sortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4 inline" /> : <ChevronDown className="ml-1 h-4 w-4 inline" />
                  )}
                </TableHead>
                <TableHead>Observações</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedMovimentacoes.map((movimentacao) => (
                <TableRow key={movimentacao.id}>
                  <TableCell>
                    {format(new Date(movimentacao.data_movimentacao), 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{movimentacao.produtos_insumos?.nome_produto}</div>
                      <div className="text-sm text-muted-foreground">
                        {movimentacao.produtos_insumos?.codigo_produto}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={movimentacao.tipo_movimentacao === 'entrada' ? 'default' : 'destructive'}>
                      {movimentacao.tipo_movimentacao === 'entrada' ? 'Entrada' : 'Saída'}
                    </Badge>
                  </TableCell>
                  <TableCell>{movimentacao.quantidade}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{movimentacao.saldo_atual}</Badge>
                  </TableCell>
                  <TableCell>{movimentacao.observacoes || '-'}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(movimentacao)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canWriteMovimentacoes && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(movimentacao)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canAdminMovimentacoes && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir esta movimentação? Esta ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(movimentacao)}>
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {sortedMovimentacoes.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma movimentação encontrada.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Visualização */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes da Movimentação</DialogTitle>
          </DialogHeader>
          {selectedMovimentacao && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Produto:</Label>
                <p>{selectedMovimentacao.produtos_insumos?.codigo_produto} - {selectedMovimentacao.produtos_insumos?.nome_produto}</p>
              </div>
              <div>
                <Label className="font-semibold">Categoria:</Label>
                <p>{selectedMovimentacao.produtos_insumos?.categoria}</p>
              </div>
              <div>
                <Label className="font-semibold">Tipo de Movimentação:</Label>
                <Badge variant={selectedMovimentacao.tipo_movimentacao === 'entrada' ? 'default' : 'destructive'}>
                  {selectedMovimentacao.tipo_movimentacao === 'entrada' ? 'Entrada' : 'Saída'}
                </Badge>
              </div>
              <div>
                <Label className="font-semibold">Quantidade:</Label>
                <p>{selectedMovimentacao.quantidade}</p>
              </div>
              <div>
                <Label className="font-semibold">Saldo Atual:</Label>
                <Badge variant="outline">{selectedMovimentacao.saldo_atual}</Badge>
              </div>
              <div>
                <Label className="font-semibold">Data da Movimentação:</Label>
                <p>{format(new Date(selectedMovimentacao.data_movimentacao), 'dd/MM/yyyy HH:mm')}</p>
              </div>
              {selectedMovimentacao.observacoes && (
                <div>
                  <Label className="font-semibold">Observações:</Label>
                  <p>{selectedMovimentacao.observacoes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}